import { makeStyles, shorthands, tokens } from '@fluentui/react-components';

/**
 * Shared section styles for the customer form components. Imported by
 * GeneralSection / AddressSection / TagsSection / etc. so the field
 * appearance stays consistent across drawer and profile-page surfaces.
 *
 * The hook (`useCustomerForm`) still owns chrome-level styles (error
 * banners, save-status badge, debug preview, etc.) that aren't part of
 * a section.
 */
export const useSectionStyles = makeStyles({
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    // Fixed row height matching the natural height of a Checkbox so
    // sections that put a control on the right (e.g. AddressSection's
    // billing-name-override checkbox) don't push the row taller than
    // sections that don't — keeps first-input alignment across
    // adjacent columns in the two-column Information layout.
    minHeight: '32px',
    ...shorthands.gap('8px'),
    '& svg': {
      color: tokens.colorNeutralForeground2,
      flexShrink: 0,
    },
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('6px'),
    width: '100%',
    paddingBottom: '12px',
    // Drop trailing padding on the last field so a section card's bottom
    // border hugs the last input — matches AddressFields, which spaces
    // siblings via a parent `gap` (no after-last-item gap).
    ':last-child': {
      paddingBottom: 0,
    },
  },
  formRow: {
    display: 'flex',
    ...shorthands.gap('12px'),
    width: '100%',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
  },
  // Inline field-level error message — sits next to the field name in red.
  fieldErrorText: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: 400,
    marginLeft: '8px',
  },
  tagField: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    ...shorthands.gap('6px'),
    minHeight: '40px',
    ...shorthands.padding('6px', '8px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    cursor: 'text',
    '&:focus-within': {
      ...shorthands.borderColor(tokens.colorBrandStroke1),
    },
  },
  tagChip: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
    ...shorthands.padding('2px', '4px', '2px', '10px'),
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    ...shorthands.borderRadius('14px'),
    fontSize: '13px',
    lineHeight: '20px',
  },
  tagDismiss: {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    ...shorthands.borderRadius('50%'),
    fontSize: '14px',
    lineHeight: '14px',
    '&:hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
      color: tokens.colorNeutralForegroundOnBrand,
    },
  },
  tagInlineInput: {
    flexGrow: 1,
    minWidth: '120px',
    ...shorthands.border('none'),
    ...shorthands.outline('none'),
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase300,
    ...shorthands.padding('4px', '6px'),
    '&::placeholder': {
      color: tokens.colorNeutralForeground3,
    },
  },
  emptyStateBlock: {
    color: tokens.colorNeutralForeground3,
    fontSize: '14px',
    ...shorthands.padding('16px', '0'),
  },
});
